import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ConfigurationBulkActionProps } from '@/features/controller/components/bulkActions/ConfigurationBulkAction'
import type { ConfigurationColumnProps } from '@/features/controller/components/bulkActions/ConfigurationColumn'
import { useMultiModalConfiguration } from '@/features/controller/configuration/hooks/useMultiModalConfiguration'
import type { ControllerConfigurationSourceUpdate } from '@/features/controller/configuration/models/controllerConfiguration'
import type { UseControllerBulkAction } from '@/features/controller/types'

const INITIAL_EMPTY_CONFIGURATION = { appJsonText: '{}', raspiPropertiesText: '' }

export function useReplaceConfigurationBulkAction(): UseControllerBulkAction<
  Omit<ConfigurationBulkActionProps, 'controllers'>,
  Omit<ConfigurationColumnProps, 'controller' | 'isSelected'>
> {
  const { t } = useTranslation()
  const { multiModalConfiguration, applyConfigurationModelUpdate, applyConfigurationSourceUpdate } =
    useMultiModalConfiguration({ initialSource: INITIAL_EMPTY_CONFIGURATION })
  const [configurationComment, setConfigurationComment] = useState<string>('')
  const [configurationUpdateInProgress, setConfigurationUpdateInProgress] = useState<boolean>(false)

  function handleLoadConfigurationFromController(update: ControllerConfigurationSourceUpdate) {
    applyConfigurationSourceUpdate(update)
    setConfigurationComment(update.comment || '')
  }

  function applyConfigurationUpdateToControllers() {
    // Clear the bulk action selection but keep the configuration state because the user has to manually apply each
    setConfigurationUpdateInProgress(true)
  }

  function onBulkActionUpdated() {
    setConfigurationUpdateInProgress(false)
  }

  return {
    bulkActionColumnProps: {
      configuration: { ...multiModalConfiguration.source, comment: configurationComment },
      configurationUpdateInProgress,
      onLoadConfigurationFromController: handleLoadConfigurationFromController,
    },
    bulkActionColumnTitle: t('controllers.bulk_actions.replace_configuration.column_title'),
    bulkActionSectionProps: {
      applyConfigurationUpdateToSelectedControllers: applyConfigurationUpdateToControllers,
      comment: configurationComment,
      configuration: multiModalConfiguration,
      configurationUpdateInProgress: configurationUpdateInProgress,
      onConfigurationModelUpdated: applyConfigurationModelUpdate,
      onConfigurationSourceUpdated: applyConfigurationSourceUpdate,
      setComment: setConfigurationComment,
    },
    controllersListUpdateInProgress: configurationUpdateInProgress,
    onBulkActionUpdated,
  }
}
